import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Eye, RotateCcw, ScanEye } from "lucide-react";

import { MedicalReportResponse, UploadedBy, UploadStatus } from "../../../api";
import { Button } from "../../../components/ui-kit/button";
import { confirm } from "../../../components/ui-kit/dialog-service";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";

// TODO(#629): extend the generated type locally until then (see
// extraction-model).
export type ReportWithExtraction = MedicalReportResponse & {
  extraction_status?: string | null;
};

// Labels are descriptors, not strings: this map is module-level data, so it
// resolves through `resolveEnumLabel` at render and follows the active locale.
const STATUSES: Record<string, { label: MessageDescriptor; color: string }> = {
  pending: { label: msg`Queued`, color: "text-gray-400" },
  processing: { label: msg`Processing…`, color: "text-yellow-500" },
  ready: { label: msg`Ready for review`, color: "text-green-600" },
  failed: { label: msg`Failed`, color: "text-red-500" },
  ingested: { label: msg`Ingested`, color: "text-blue-500" },
  rejected: { label: msg`Rejected`, color: "text-gray-500" },
};

const NOT_STARTED = msg`Not started`;

/** How often a screen waiting on an extraction asks again: a run takes
 *  seconds to a couple of minutes, so a finer interval only adds requests. */
export const EXTRACTION_POLL_MS = 5000;

export function isExtractionRunning(report: ReportWithExtraction): boolean {
  return (
    report.extraction_status === "pending" ||
    report.extraction_status === "processing"
  );
}

export function isPatientUpload(report: ReportWithExtraction): boolean {
  return report.uploaded_by === UploadedBy.patient;
}

export function extractionStatusLabel(report: ReportWithExtraction): string {
  if (report.upload_status !== UploadStatus.success) return "";
  if (!report.extraction_status) return resolveEnumLabel(NOT_STARTED);
  // A status the frontend does not know is shown as it came, rather than blank.
  const known = STATUSES[report.extraction_status]?.label;
  return known ? resolveEnumLabel(known) : report.extraction_status;
}

export function extractionStatusColor(
  status: string | undefined | null,
): string {
  return STATUSES[status ?? ""]?.color ?? "text-gray-400";
}

/**
 * The Extraction table cell, shared by the reports table and the review queue
 * so the two can never disagree about a status label or which action it
 * offers.
 */
export function ExtractionCell({
  report,
  onReview,
  onRetry,
}: {
  report: ReportWithExtraction;
  onReview: (report: ReportWithExtraction) => void;
  onRetry: (report: ReportWithExtraction) => void;
}) {
  const { t } = useLingui();
  const status = report.extraction_status;
  const rerunAction =
    report.upload_status === UploadStatus.success
      ? RERUNS[status ?? ""]
      : undefined;

  async function rerun() {
    if (rerunAction?.confirm) {
      const confirmed = await confirm(
        t`The AI reads the document again and replaces the current results, including any corrections saved in review. The new results need to be reviewed again.`,
        t`Re-run extraction?`,
        { confirmLabel: t`Re-run` },
      );
      if (!confirmed) return;
    }
    onRetry(report);
  }

  return (
    <span className="flex justify-center items-center gap-2">
      <span className={extractionStatusColor(status)}>
        {extractionStatusLabel(report)}
      </span>
      {status === "ready" && (
        <Button
          variant="tertiary"
          size="xs"
          label={t`Review`}
          icon={ScanEye}
          onClick={() => onReview(report)}
        />
      )}
      {status === "ingested" && (
        <Button
          variant="tertiary"
          size="xs"
          label={t`View`}
          icon={Eye}
          onClick={() => onReview(report)}
        />
      )}
      {rerunAction && (
        <Button
          variant="tertiary"
          size="xs"
          label={resolveEnumLabel(rerunAction.label)}
          icon={RotateCcw}
          onClick={() => void rerun()}
        />
      )}
    </span>
  );
}

// What the retry endpoint accepts, minus `pending`: that one is already
// queued. The server refuses `processing` (a second run of the same document)
// and `ingested` (its entities are already in the patient's record). A
// document awaiting review offers its re-run in the review panel, beside the
// result it would replace, so `ready` is left out too. Keyed like STATUSES,
// with "" for a report never extracted. A new run supersedes the current
// result, and with it any corrections saved in review, so only a result worth
// keeping asks first.
const RERUNS: Record<string, { label: MessageDescriptor; confirm: boolean }> = {
  "": { label: msg`Run extraction`, confirm: false },
  failed: { label: msg`Retry`, confirm: false },
  rejected: { label: msg`Re-run extraction`, confirm: true },
};
